import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  fetchProjectDetail, 
  updateProject, 
  archiveProject,
  fetchProjectReflections 
} from '../../services/dashboardService';
import { 
  ArrowLeft, 
  FolderKanban,
  MessageSquare,
  Calendar,
  Users,
  Loader2,
  Pencil,
  Check,
  X,
  Trash2,
  TrendingUp,
  ChevronRight,
  Tag,
  Clock,
  Target
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-blue-500' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-yellow-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-zinc-500' }
];

const ROLE_COLORS = {
  lead: 'bg-purple-500/10 text-purple-400',
  contributor: 'bg-blue-500/10 text-blue-400',
  stakeholder: 'bg-yellow-500/10 text-yellow-400',
  blocker: 'bg-red-500/10 text-red-400',
  sponsor: 'bg-green-500/10 text-green-400'
};

const SITUATION_COLORS = {
  feedback: 'bg-blue-500/10 text-blue-400',
  delegation: 'bg-green-500/10 text-green-400',
  difficult_conversation: 'bg-red-500/10 text-red-400',
  decision_making: 'bg-purple-500/10 text-purple-400',
  project_update: 'bg-amber-500/10 text-amber-400',
  one_to_one: 'bg-yellow-500/10 text-yellow-400',
  team_conflict: 'bg-orange-500/10 text-orange-400',
  general: 'bg-zinc-500/10 text-zinc-400'
};

const ProjectDetailPage = () => {
  const { alias } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadProject = async () => {
      try {
        const [projectData, reflectionsData] = await Promise.all([
          fetchProjectDetail(alias),
          fetchProjectReflections(alias).catch(() => ({ reflections: [] }))
        ]);
        setProject(projectData);
        setReflections(reflectionsData.reflections || []);
        setEditedProject(projectData);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && alias) {
      loadProject();
    }
  }, [isAuthenticated, alias, navigate]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedProject({ ...project });
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProject(project);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const updates = {
        name: editedProject.name,
        description: editedProject.description,
        status: editedProject.status,
        tags: editedProject.tags
      };
      
      const updated = await updateProject(alias, updates);
      setProject(updated);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    try {
      await archiveProject(alias);
      navigate('/dashboard/projects', { replace: true });
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const handleTagsChange = (e) => {
    const tagsStr = e.target.value;
    const tags = tagsStr.split(',').map(t => t.trim()).filter(Boolean);
    setEditedProject({ ...editedProject, tags });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <header className="border-b border-zinc-800">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/dashboard/projects" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              Back to Projects
            </Link>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-16 text-center">
          <FolderKanban className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Project not found</h2>
          <p className="text-zinc-400">{error || `Could not find project "${alias}".`}</p>
        </main>
      </div>
    );
  }

  const statusConfig = STATUS_OPTIONS.find(s => s.value === project.status) || STATUS_OPTIONS[0];
  const peopleCount = project.people_involved?.length || 0;

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/dashboard/projects" className="flex items-center gap-2 text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Project header */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
          {isEditing ? (
            /* Edit Mode */
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Project Name</label>
                <input
                  type="text"
                  value={editedProject.name}
                  onChange={(e) => setEditedProject({ ...editedProject, name: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  data-testid="project-name-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Description</label>
                <textarea
                  value={editedProject.description || ''}
                  onChange={(e) => setEditedProject({ ...editedProject, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                  placeholder="Brief description of this project..."
                  data-testid="project-description-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Status</label>
                <select
                  value={editedProject.status}
                  onChange={(e) => setEditedProject({ ...editedProject, status: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  data-testid="project-status-select"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={(editedProject.tags || []).join(', ')}
                  onChange={handleTagsChange}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="e.g. infrastructure, q2-priority, migration"
                  data-testid="project-tags-input"
                />
              </div>
              
              {saveError && (
                <p className="text-red-400 text-sm">{saveError}</p>
              )}
              
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  data-testid="project-save-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                  data-testid="project-cancel-button"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* View Mode */
            <>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FolderKanban className="w-8 h-8 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-zinc-500 text-sm font-mono">{project.alias}</span>
                    <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${
                      statusConfig.value === 'active' ? 'bg-blue-500/10 text-blue-400' :
                      statusConfig.value === 'completed' ? 'bg-green-500/10 text-green-400' :
                      statusConfig.value === 'on_hold' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-zinc-500/10 text-zinc-400'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.color}`}></span>
                      {statusConfig.label}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-white mb-2">{project.name}</h1>
                  
                  {project.description && (
                    <p className="text-zinc-300 mb-4">{project.description}</p>
                  )}
                  
                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleStartEdit}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                      data-testid="project-edit-button"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                    <Link
                      to={`/dashboard/chat?project=${encodeURIComponent(project.alias)}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                      data-testid="chat-about-project-button"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Chat about this
                    </Link>
                    {project.status !== 'cancelled' && (
                      <button
                        onClick={() => setShowArchiveConfirm(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-red-600/20 text-zinc-400 hover:text-red-400 rounded-lg transition-colors"
                        data-testid="project-archive-button"
                      >
                        <Trash2 className="w-4 h-4" />
                        Archive
                      </button>
                    )}
                  </div>
                  
                  {/* Stats */}
                  <div className="mt-4 flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <MessageSquare className="w-4 h-4" />
                      {project.mention_count || 0} mention{(project.mention_count || 0) !== 1 ? 's' : ''}
                    </div>
                    {project.first_mentioned && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Calendar className="w-4 h-4" />
                        Started {new Date(project.first_mentioned).toLocaleDateString()}
                      </div>
                    )}
                    {project.target_end_date && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Target className="w-4 h-4" />
                        Target: {new Date(project.target_end_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className="w-4 h-4 text-zinc-500" />
                    {project.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-zinc-800 text-zinc-400 text-sm rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-white mb-2">Archive Project?</h3>
              <p className="text-zinc-400 mb-6">
                This will mark "{project.name}" as cancelled. You can still view it in the project list.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchive}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  data-testid="confirm-archive-button"
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        )}

        {/* People Involved */}
        {peopleCount > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-zinc-400" />
              Team Members
              <span className="text-zinc-500 font-normal">({peopleCount})</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {project.people_involved.map((person, idx) => (
                <Link
                  key={idx}
                  to={`/dashboard/people/${encodeURIComponent(person.alias)}`}
                  className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  <div className="w-10 h-10 bg-zinc-700 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">{person.alias}</p>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${ROLE_COLORS[person.role] || 'bg-zinc-600/50 text-zinc-400'}`}>
                      {person.role}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related Reflections */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-zinc-400" />
            Related Reflections
            <span className="text-zinc-500 font-normal">({reflections.length})</span>
          </h2>

          {reflections.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
              <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No reflections linked to this project yet</p>
              <p className="text-zinc-500 text-sm mt-1">
                Mention "{project.alias}" or "{project.name}" in your reflections to link them here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {reflections.map((reflection, index) => (
                <Link
                  key={index}
                  to={`/dashboard/reflections/${reflection.conversation_id}`}
                  className="block bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {reflection.situation_category && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${SITUATION_COLORS[reflection.situation_category] || SITUATION_COLORS.general}`}>
                          {reflection.situation_category.replace('_', ' ')}
                        </span>
                      )}
                      {reflection.subject && (
                        <span className="text-zinc-300 text-sm truncate max-w-[200px]">
                          {reflection.subject}
                        </span>
                      )}
                    </div>
                    <span className="text-zinc-500 text-sm flex-shrink-0">
                      {new Date(reflection.created_at || reflection.last_interaction).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectDetailPage;
